/**
 * CategoriesTab — sidebar category list + detail panel with items / tickets / returns sub-tabs.
 *
 * Props:
 *   data           — full API response
 *   maxCatRevenue  — max revenueCents across all categories (for bar widths)
 *   selectedCat    — currently selected category name (or null)
 *   setSelectedCat — setter
 *   goToCategory   — function(catName, tab?) — used only by overview "see all" nav (not needed here)
 */
import React, { useState, useMemo, useEffect } from 'react';
import { fmt$, fmtN, pct } from '../../../utils/formatUtils';
import { getTicketType, TICKET_TYPE_META } from '../squareDashboardUtils';
import ItemsTable from './ItemsTable';
import TicketList from './TicketList';
import CategoryTrendsTab from './CategoryTrendsTab';

export default function CategoriesTab({ data, maxCatRevenue, selectedCat, setSelectedCat }) {
  const [catTab,        setCatTab]        = useState('items');
  const [itemSearch,    setItemSearch]    = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState('all');

  // Reset sub-tab state whenever the selected category changes
  useEffect(() => {
    setCatTab('items');
    setItemSearch('');
    setItemTypeFilter('all');
  }, [selectedCat]);

  const catDetail = useMemo(
    () => (selectedCat ? data.categories.find(c => c.name === selectedCat) || null : null),
    [selectedCat, data]
  );

  const filteredItems = useMemo(() => {
    if (!catDetail) return [];
    let list;
    if (itemTypeFilter !== 'all') {
      const matchingTickets = (catDetail.ticketList || []).filter(
        tkt => getTicketType(tkt) === itemTypeFilter
      );
      const itemMap = {};
      matchingTickets.forEach(tkt => {
        (tkt.lines || []).forEach(line => {
          const k = line.name.toLowerCase();
          if (!itemMap[k]) itemMap[k] = { name: line.name, qty: 0, revenueCents: 0, discountCents: 0, returnQty: 0, returnRevenue: '0.00' };
          itemMap[k].qty          += line.qty;
          itemMap[k].revenueCents += line.revenueCents;
          if (tkt.revenueCents > 0) {
            itemMap[k].discountCents += Math.round((tkt.discountCents || 0) * line.revenueCents / tkt.revenueCents);
          }
        });
      });
      list = Object.values(itemMap).map(item => ({
        ...item,
        revenue:  (item.revenueCents  / 100).toFixed(2),
        discount: (item.discountCents / 100).toFixed(2),
      })).sort((a, b) => b.qty - a.qty);
    } else {
      list = catDetail.items;
    }
    const q = itemSearch.trim().toLowerCase();
    return q ? list.filter(i => i.name.toLowerCase().includes(q)) : list;
  }, [catDetail, itemSearch, itemTypeFilter]);

  const typeSummary = useMemo(() => {
    if (!catDetail || itemTypeFilter === 'all') return null;
    const matching = (catDetail.ticketList || []).filter(tkt => getTicketType(tkt) === itemTypeFilter);
    const revCents  = matching.reduce((s, t) => s + (t.revenueCents  || 0), 0);
    const discCents = matching.reduce((s, t) => s + (t.discountCents || 0), 0);
    const itemQty   = matching.reduce((s, t) => s + (t.lines || []).reduce((ls, l) => ls + l.qty, 0), 0);
    return { count: matching.length, revCents, discCents, itemQty };
  }, [catDetail, itemTypeFilter]);

  return (
    <div className="sq__pane sq__cat-layout">
      {/* ── Sidebar ── */}
      <aside className="sq__sidebar">
        <div className="sq__sidebar-title">
          Categories
          <span className="sq__sidebar-badge">{data.categories.length}</span>
        </div>
        {data.categories.map(cat => (
          <button
            key={cat.name}
            className={`sq__cat-btn${selectedCat === cat.name ? ' active' : ''}`}
            onClick={() => setSelectedCat(cat.name)}
          >
            <div className="sq__catbtn-row1">
              <span className="sq__catbtn-name">{cat.name}</span>
              {cat.returnQty > 0 && <span className="sq__catbtn-ret">&#x21A9;&#xFE0F; {cat.returnQty}</span>}
            </div>
            <div className="sq__catbtn-row2">
              <span className="sq__catbtn-rev">{fmt$(cat.revenue)}</span>
              <span className="sq__catbtn-qty">{fmtN(cat.itemQty)} items</span>
            </div>
            <div className="sq__catbtn-bar">
              <div className="sq__catbtn-bar-fill" style={{ width: pct(cat.revenueCents, maxCatRevenue) }} />
            </div>
          </button>
        ))}
      </aside>

      {/* ── Detail panel ── */}
      <div className="sq__cat-panel">
        {!catDetail ? (
          <div className="sq__empty-state">
            <div className="sq__es-icon">&#x1F4C1;</div>
            <div className="sq__es-title">Select a category</div>
            <div className="sq__es-sub">Choose any category to see items, tickets, and returns.</div>
          </div>
        ) : (
          <>
            {/* Panel header */}
            <div className="sq__panel-header">
              <div className="sq__panel-title">{catDetail.name}</div>
              <div className="sq__panel-metrics-scroll">
                <div className="sq__panel-kpis">
                  <div className="sq__pkpi sq__pkpi--green"><div className="sq__pkpi-val">{fmt$(catDetail.revenue)}</div><div className="sq__pkpi-lbl">Revenue</div></div>
                  <div className="sq__pkpi"><div className="sq__pkpi-val">{fmtN(catDetail.itemQty)}</div><div className="sq__pkpi-lbl">Items</div></div>
                  <div className="sq__pkpi"><div className="sq__pkpi-val">{fmtN(catDetail.tickets)}</div><div className="sq__pkpi-lbl">Tickets</div></div>
                  <div className="sq__pkpi sq__pkpi--amber"><div className="sq__pkpi-val">{fmt$(catDetail.discount)}</div><div className="sq__pkpi-lbl">Discounts</div></div>
                  {catDetail.returnQty > 0 && (
                    <div className="sq__pkpi sq__pkpi--rose">
                      <div className="sq__pkpi-val">{fmtN(catDetail.returnQty)}</div>
                      <div className="sq__pkpi-lbl">Returns</div>
                    </div>
                  )}
                </div>

                {/* Category type chips */}
                <div className="sq__cat-type-chips">
                  {Object.entries(TICKET_TYPE_META).map(([k, v]) => {
                    const tkts = (catDetail.ticketList || []).filter(t => getTicketType(t) === k);
                    if (tkts.length === 0) return null;
                    const rev = tkts.reduce((s, t) => s + (t.revenueCents || 0), 0);
                    return (
                      <div key={k} className={`sq__cat-type-chip sq__cat-type-chip--${k}`}>
                        <span className="sq__cat-type-chip-label">{v.label}</span>
                        <span className="sq__cat-type-chip-sep">&middot;</span>
                        <span className="sq__cat-type-chip-count">{tkts.length}</span>
                        <span className="sq__cat-type-chip-sep">&middot;</span>
                        <span className="sq__cat-type-chip-rev">{fmt$(rev / 100)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="sq__ptabs">
              <button className={`sq__ptab${catTab === 'items' ? ' active' : ''}`} onClick={() => setCatTab('items')}>
                &#x1F4E6; Items ({catDetail.items.length})
              </button>
              <button className={`sq__ptab${catTab === 'tickets' ? ' active' : ''}`} onClick={() => setCatTab('tickets')}>
                &#x1F3AB; Tickets ({catDetail.tickets})
              </button>
              {catDetail.returnQty > 0 && (
                <button
                  className={`sq__ptab sq__ptab--rose${catTab === 'returns' ? ' active' : ''}`}
                  onClick={() => setCatTab('returns')}
                >
                  &#x21A9;&#xFE0F; Returns ({catDetail.returnQty})
                </button>
              )}
              <button
                className={`sq__ptab sq__ptab--indigo${catTab === 'trends' ? ' active' : ''}`}
                onClick={() => setCatTab('trends')}
              >
                📈 Trends
              </button>
            </div>

            {/* Sub-tab body */}
            <div className="sq__panel-body">
              {catTab === 'items' && (
                <>
                  <input
                    className="sq__search"
                    type="search"
                    placeholder="Search items\u2026"
                    value={itemSearch}
                    onChange={e => setItemSearch(e.target.value)}
                  />
                  {/* Type filter pills */}
                  <div className="sq__tkt-type-pills" style={{ paddingBottom: 8 }}>
                    <span className="sq__tkt-type-label">Filter by type:</span>
                    <button
                      className={`sq__tkt-typepill${itemTypeFilter === 'all' ? ' active' : ''}`}
                      onClick={() => setItemTypeFilter('all')}
                    >
                      All
                    </button>
                    {Object.entries(TICKET_TYPE_META).map(([k, v]) => (
                      <button
                        key={k}
                        className={`sq__tkt-typepill sq__tkt-typepill--${k}${itemTypeFilter === k ? ' active' : ''}`}
                        onClick={() => setItemTypeFilter(k)}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>

                  {/* Type summary bar */}
                  {typeSummary && (
                    <div className="sq__type-summary">
                      <span className="sq__ts-label">{TICKET_TYPE_META[itemTypeFilter].label}</span>
                      <span className="sq__ts-sep">&#x2014;</span>
                      <span className="sq__ts-stat">{fmtN(typeSummary.count)} tickets</span>
                      <span className="sq__ts-dot">&#xB7;</span>
                      <span className="sq__ts-stat">{fmtN(typeSummary.itemQty)} items</span>
                      <span className="sq__ts-dot">&#xB7;</span>
                      <span className="sq__ts-rev">{fmt$(typeSummary.revCents / 100)} revenue</span>
                      {typeSummary.discCents > 0 && (
                        <>
                          <span className="sq__ts-dot">&#xB7;</span>
                          <span className="sq__ts-disc">-{fmt$(typeSummary.discCents / 100)} disc</span>
                        </>
                      )}
                    </div>
                  )}

                  <ItemsTable
                    items={filteredItems}
                    ticketList={catDetail.ticketList}
                    typeFilter={itemTypeFilter}
                  />
                </>
              )}

              {catTab === 'tickets' && <TicketList tickets={catDetail.ticketList || []} />}

              {catTab === 'returns' && (
                catDetail.items.filter(i => i.returnQty > 0).length === 0
                  ? <p className="sq__empty">No returns in this category.</p>
                  : <ItemsTable items={catDetail.items.filter(i => i.returnQty > 0)} returnsOnly />
              )}

              {catTab === 'trends' && <CategoryTrendsTab catDetail={catDetail} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
